<script setup lang="ts">
import { useAnnouncer } from '@vue-a11y/announcer';
import { whenever } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import Modal from '@/components/base/layout/Modal.vue';
import SignInForm from '@/components/login/SignInForm.vue';
import SignUpForm from '@/components/login/SignUpForm.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useLoginModal } from '@/composables/useLoginModal';
import { USER_EMAIL_COOKIE } from '@/lib/personalization/common';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { getCookie } from '@/utils/isomorphic/cookie';

type Step = 'sign-in' | 'sign-up';

const { handleClose, state } = useLoginModal();

const store = useStore();
const router = useRouter();

const { polite } = useAnnouncer();

const currentStep = ref<Step>(state.initialStep);
const email = ref(getCookie(USER_EMAIL_COOKIE, false) ?? '');

const { loadLineItemExpansions, setCustomerEmail } = useCart(store);
const signInCallback = useCallback(async (options?: { isBusinessAccount: boolean }) => {
  polite('You have successfully signed in.');
  useAutoDelivery().initSdkClient();
  await loadLineItemExpansions();

  const isSpa = !!router;
  const navigateFn = isSpa ? router.push : window.location.assign;

  if (state.destination && !options?.isBusinessAccount) {
    return navigateFn(state.destination);
  }
  if (state.callback) {
    await state.callback.execute();
  }

  if (options?.isBusinessAccount && !state.isCheckout) {
    sessionStorage.setItem('B2BAccountSource', 'Account Creation');
    return navigateFn('/business/register');
  }

  handleClose();

  return undefined;
});

const guestSignInCallback = useCallback(async (guestEmail?: string) => {
  if (guestEmail) await setCustomerEmail(guestEmail);
  await state.callback?.execute();
  handleClose();
});

const onChangeEmail = (step: Step, newEmail?: string) => {
  email.value = newEmail ?? '';
  currentStep.value = step;
};

whenever(
  () => state.isOpen,
  () => {
    currentStep.value = state.initialStep;
  },
);
</script>

<template>
  <Modal
    aria-labelledby="login-modal-label"
    data-test="login-modal"
    breakpoint="sm"
    :class="{ 'pointer-events-none': signInCallback.isPending }"
    :hasControls="false"
    :isOpen="state.isOpen"
    skipFirstElementOnFocus
    width="sm:max-w-md"
    @handle-close="handleClose"
  >
    <template #body>
      <transition name="fade" mode="out-in">
        <SignInForm
          v-if="currentStep === 'sign-in'"
          :email
          :guestSignInCallback
          :isCheckout="state.isCheckout"
          :submitCallback="signInCallback"
          @change-email="(email) => onChangeEmail('sign-up', email)"
        >
          <template #header="{ title }">
            <span
              class="text-xl font-bold sm:text-2xl font-sofia-pro"
              id="login-modal-label"
              data-test="login-modal-title"
            >
              {{ title }}
            </span>
          </template>
        </SignInForm>
        <SignUpForm
          v-else
          :email
          :isCheckout="state.isCheckout"
          :selectBusiness="state.selectBusiness"
          @signed-in="signInCallback.execute"
          @change-email="(email) => onChangeEmail('sign-in', email)"
        >
          <template #header="{ title }">
            <span class="text-xl font-bold sm:text-2xl font-sofia-pro" id="login-modal-label">
              {{ title }}
            </span>
          </template>
        </SignUpForm>
      </transition>
    </template>
  </Modal>
</template>
